import React, {useRef} from 'react';
import Select from "react-select";
import styles from "./SelectDropdown.module.css";
import {getSelectorStyles} from "./selectorStyles";
import classnames from "classnames";

export type SelectOption<PROPERTY> = { label: string, value: PROPERTY };

interface Props<PROPERTY> {
    title: string;
    titleAccessory?: React.ReactNode;
    isRequired?: boolean;
    value?: PROPERTY;
    handleChange: (v: PROPERTY) => any;
    options: SelectOption<PROPERTY>[];
    errorMessage?: string;
    disabled?: boolean;
    selectFirstOptionByDefault?: boolean;
    isClearable?: boolean;
    placeholder?: string;
    /**
     * Renders the open menu on top of the page instead of inside the field, so a container that
     * clips its overflow or controls drawn after the field cannot hide part of the options.
     */
    renderMenuOnTop?: boolean;
    /**
     * Renders only a smaller field, for headers and toolbars: the title becomes its accessible label, and no
     * room is kept for a title or an error message.
     */
    compact?: boolean;
}


export const SelectDropdown = <PROPERTY, >({
                                               title,
                                               titleAccessory,
                                               isRequired,
                                               value,
                                               handleChange,
                                               options,
                                               errorMessage,
                                               disabled = false,
                                               selectFirstOptionByDefault = true,
                                               isClearable = false,
                                               placeholder,
                                               renderMenuOnTop = false,
                                               compact = false
                                           }: Props<PROPERTY>) => {

    const getSelectOptionByValue = () => {
        const optionValue = options.find(o => o.value === value)

        if (!optionValue && selectFirstOptionByDefault) {
            const firstOption = options[0]
            handleChange(firstOption.value)
            return firstOption
        } else {
            return optionValue
        }
    }

    const wrapperRef = useRef<HTMLDivElement>(null);

    // A menu drawn on top of the page does not follow the field, so close it when anything around the
    // field scrolls; scrolling the option list itself keeps it open.
    const closeMenuOnScroll = (event: Event) =>
        event.target instanceof Node && !!wrapperRef.current && event.target.contains(wrapperRef.current);

    return <div className={styles.wrapper} ref={wrapperRef}>
        {!compact && <div className={styles.titleWrapper}>
            <span className={classnames(styles.title, disabled && styles.disabledTitle)}>{title}</span>
            {(isRequired && !disabled) && <span className={styles.asterisk}>*</span>}
            {titleAccessory}
        </div>}
        <Select
            isDisabled={disabled}
            isClearable={isClearable}
            value={getSelectOptionByValue() ?? null}
            onChange={v => {
                handleChange(v ? v.value as PROPERTY : undefined as PROPERTY)
            }}
            options={options}
            styles={getSelectorStyles(errorMessage, compact)}
            aria-label={compact ? title : undefined}
            placeholder={placeholder}
            menuPortalTarget={renderMenuOnTop ? document.body : undefined}
            menuPosition={renderMenuOnTop ? "fixed" : "absolute"}
            menuPlacement={renderMenuOnTop ? "auto" : "bottom"}
            closeMenuOnScroll={renderMenuOnTop ? closeMenuOnScroll : false}
        />
        {!compact && <div style={{visibility: `${!errorMessage ? "hidden" : "visible"}`}} className={styles.errorWrapper}>
            <img src={"/alertIcon.svg"} alt={"alert icon"}/>
            <span className={styles.errorMessage}>{errorMessage}</span>
        </div>}
    </div>
}
